import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Users, Truck, Store, Building2, Megaphone, Sparkles, ShoppingBag } from "lucide-react";
import { trackEvent } from "@/lib/retention";

/**
 * Phase 11.4 → 11.6 — The Lifted Growth Engine.
 *
 * Animated SVG ecosystem diagram:
 *   center node = LiftedAtTheMoment
 *   5 satellite nodes = Customer · Affiliate · Driver · Partner Store · Franchise
 *
 * Pulse animation is CSS-only (no animation library). Respects
 * prefers-reduced-motion via index.css.
 *
 * Phase 11.6 correction: Content Creator and Community Growth are NOT
 * ecosystem roles. Content creators / influencers / promoters /
 * photographers / videographers / designers / community builders are
 * FUNCTIONS of the Affiliate Program (rendered in the Heartbeat section
 * on /ecosystem). Community is the OUTCOME of the 5 roles, not a role
 * itself.
 */
const NODES = [
  { key: "customer",  label: "Customer",        sub: "Shop the lifestyle",       icon: ShoppingBag, to: "/shop",            event: "growth_engine_node_click" },
  { key: "affiliate", label: "Affiliate",       sub: "Marketing engine",         icon: Megaphone,   to: "/affiliate-zone",  event: "affiliate_role_click" },
  { key: "driver",    label: "Driver",          sub: "Earn on the road",         icon: Truck,        to: "/supplies",        event: "driver_opportunity_click" },
  { key: "partner",   label: "Partner Store",   sub: "Plug into the network",    icon: Store,        to: "/ecosystem#role-partner",     event: "partner_opportunity_click" },
  { key: "franchise", label: "Franchise",       sub: "Operate a territory",      icon: Building2,    to: "/ecosystem#role-franchise",   event: "franchise_opportunity_click" },
];

const RING_RADIUS = 180; // px — used by the SVG diagram

export default function GrowthEngineSection({ id = "home-growth-engine" }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    // Fire a single growth_engine_view when the section enters the viewport.
    if (!("IntersectionObserver" in window)) {
      trackEvent("growth_engine_view");
      return;
    }
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          trackEvent("growth_engine_view");
          io.disconnect();
          break;
        }
      }
    }, { threshold: 0.25 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  return (
    <section
      id={id}
      ref={ref}
      data-testid={id}
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16"
    >
      <div className="text-center max-w-3xl mx-auto fade-up">
        <div className="eyebrow inline-flex items-center gap-2 justify-center"><Users className="h-3 w-3" /> The Lifted Growth Engine</div>
        <h2 className="font-anton text-4xl sm:text-5xl uppercase mt-3">
          One ecosystem.<br />Many opportunities.
        </h2>
        <p className="text-lifted-dim mt-4 text-sm sm:text-base">
          Powered by people building the Lifted lifestyle. Affiliates create the awareness that fuels every other role —
          customers, drivers, partner stores, and future franchisees all grow together.
        </p>
      </div>

      {/* Animated ecosystem diagram */}
      <div className="mt-12 flex flex-col lg:flex-row items-center gap-10">
        <div className="lg:w-1/2 w-full flex justify-center" data-testid="growth-engine-diagram">
          <svg viewBox="0 0 480 480" className="w-full max-w-[480px] aspect-square growth-engine-svg" aria-hidden="true">
            <defs>
              <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%"  stopColor="#9DD034" stopOpacity="0.55" />
                <stop offset="60%" stopColor="#9DD034" stopOpacity="0.10" />
                <stop offset="100%" stopColor="#9DD034" stopOpacity="0" />
              </radialGradient>
              <radialGradient id="nodeGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%"  stopColor="#8B3FC2" stopOpacity="0.55" />
                <stop offset="100%" stopColor="#8B3FC2" stopOpacity="0" />
              </radialGradient>
            </defs>
            {/* connection lines */}
            {NODES.map((_, i) => {
              const angle = (i / NODES.length) * 2 * Math.PI - Math.PI / 2;
              const x = 240 + Math.cos(angle) * RING_RADIUS;
              const y = 240 + Math.sin(angle) * RING_RADIUS;
              return (
                <line key={`line-${i}`}
                      x1={240} y1={240} x2={x} y2={y}
                      stroke="#9DD034" strokeOpacity="0.3" strokeWidth="1"
                      className="growth-engine-line" />
              );
            })}
            {/* center glow */}
            <circle cx={240} cy={240} r={120} fill="url(#centerGlow)" />
            <circle cx={240} cy={240} r={56} fill="#0B0B10" stroke="#9DD034" strokeWidth="1.5" />
            <text x={240} y={236} textAnchor="middle" fontFamily="Anton, sans-serif"
                  fontSize="13" letterSpacing="2" fill="#F3F5F0">LIFTED</text>
            <text x={240} y={252} textAnchor="middle" fontFamily="Anton, sans-serif"
                  fontSize="11" letterSpacing="2" fill="#9DD034">AT THE MOMENT</text>
            {/* satellite nodes */}
            {NODES.map((n, i) => {
              const angle = (i / NODES.length) * 2 * Math.PI - Math.PI / 2;
              const x = 240 + Math.cos(angle) * RING_RADIUS;
              const y = 240 + Math.sin(angle) * RING_RADIUS;
              return (
                <g key={n.key} className="growth-engine-node" style={{ animationDelay: `${i * 0.4}s` }}>
                  <circle cx={x} cy={y} r={42} fill="url(#nodeGlow)" />
                  <circle cx={x} cy={y} r={26} fill="#13131C" stroke="#8B3FC2" strokeWidth="1.2" />
                  <text x={x} y={y + 4} textAnchor="middle" fontFamily="Anton, sans-serif"
                        fontSize="9" letterSpacing="1.5" fill="#F3F5F0">
                    {n.label.split(" ")[0].toUpperCase()}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

        {/* Role cards */}
        <div className="lg:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
          {NODES.map((n) => {
            const Icon = n.icon;
            return (
              <Link
                key={n.key}
                to={n.to}
                data-testid={`growth-engine-node-${n.key}`}
                onClick={() => trackEvent(n.event, { node: n.key, surface: "growth_engine" })}
                className="glass-panel p-4 hover:-translate-y-1 transition-transform group"
              >
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-lifted-green/15 border border-lifted-green/30 grid place-items-center text-lifted-green">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="font-anton uppercase text-base text-lifted-text group-hover:text-lifted-green transition-colors">{n.label}</div>
                    <div className="text-[0.7rem] text-lifted-dim mt-0.5">{n.sub}</div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Chain-of-growth explainer */}
      <div className="mt-14 glass-panel p-6 sm:p-8" data-testid="growth-engine-chain">
        <div className="eyebrow flex items-center gap-2"><Sparkles className="h-3 w-3" /> How it grows</div>
        <p className="font-anton uppercase text-2xl sm:text-3xl mt-2 leading-tight max-w-4xl">
          Affiliates create awareness. Awareness brings customers. Customers create sales.
          Sales create deliveries. Deliveries need drivers. Drivers need <span className="text-lifted-green">partner stores</span>.
          Partner stores feed <span className="text-lifted-green">franchise opportunities</span>. The ecosystem expands.
        </p>
        <p className="text-[0.72rem] text-lifted-mute mt-4 max-w-3xl leading-relaxed">
          Driver, partner, and franchise applications go through Lifted approval and screening. Introducing a
          candidate does not guarantee acceptance. Every role meets Lifted standards.
        </p>
      </div>
    </section>
  );
}
